import {
  Experiment,
  type ExperimentClient,
  type ExperimentUser,
} from '@amplitude/experiment-js-client'
import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { v4 as uuidv4 } from 'uuid'

import { safeLocalStorage } from '@hooks/useStorage'

import { getAmplitudeIdentifyProperties } from '@analytics/amplitude/identifyProperties'
import { useAnalyticsConsent } from '@analytics/consent'

import { AuthSessionContext, type MaybeAuthSession } from './AuthContext'
import { defaultFeatureFlags, resolveFeatureFlags, type FeatureFlags } from './FeatureFlags'

const FEATURE_FLAGS_INSTANCE_NAME = 'tickmill-clientarea-feature-flags'
const FEATURE_FLAGS_DEVICE_ID_STORAGE_KEY = 'tmca:feature-flags:device-id'

let featureFlagsClient: ExperimentClient | null | undefined

const getExperimentApiKey = (): string | undefined => {
  return import.meta.env.VITE_AMPLITUDE_API_KEY as string | undefined
}

let sessionDeviceId: string | undefined

const getOrCreateFeatureFlagsDeviceId = (persist: boolean): string | undefined => {
  if (typeof window === 'undefined') return undefined

  const existingDeviceId = safeLocalStorage.getItem(FEATURE_FLAGS_DEVICE_ID_STORAGE_KEY)
  if (existingDeviceId) return existingDeviceId

  sessionDeviceId = sessionDeviceId ?? uuidv4()
  if (persist) {
    safeLocalStorage.setItem(FEATURE_FLAGS_DEVICE_ID_STORAGE_KEY, sessionDeviceId)
  }
  return sessionDeviceId
}

const getFeatureFlagsClient = (): ExperimentClient | null => {
  if (featureFlagsClient !== undefined) return featureFlagsClient
  if (typeof window === 'undefined') {
    featureFlagsClient = null
    return featureFlagsClient
  }

  const apiKey = getExperimentApiKey()
  if (!apiKey) {
    featureFlagsClient = null
    return featureFlagsClient
  }

  featureFlagsClient = Experiment.initialize(apiKey, {
    automaticExposureTracking: false,
    fallbackVariant: { value: 'false' },
    fetchOnStart: false,
    instanceName: FEATURE_FLAGS_INSTANCE_NAME,
    pollOnStart: false,
    retryFetchOnFailure: true,
    serverZone: 'EU',
  })

  return featureFlagsClient
}

const createExperimentUser = (auth: MaybeAuthSession, persistDeviceId: boolean): ExperimentUser => {
  const userProperties: ExperimentUser['user_properties'] = {
    environment: (import.meta.env.VITE_ENV as string | undefined) ?? 'dev',
    platform: (import.meta.env.VITE_PLATFORM as string | undefined) ?? 'web_client_area',
    ...getAmplitudeIdentifyProperties(auth),
  }

  return {
    device_id: getOrCreateFeatureFlagsDeviceId(persistDeviceId),
    user_id: auth?.id,
    user_properties: userProperties,
  }
}

export const FeatureFlagsContext = createContext<FeatureFlags | undefined>(undefined)
FeatureFlagsContext.displayName = 'FeatureFlagsContext'

type FeatureFlagsStatus = {
  flagsReady: boolean
}

const FeatureFlagsStatusContext = createContext<FeatureFlagsStatus | undefined>(undefined)
FeatureFlagsStatusContext.displayName = 'FeatureFlagsStatusContext'

export const FeatureFlagsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [auth] = useContext(AuthSessionContext)

  const hasAnalyticsConsent = useAnalyticsConsent()
  const persistDeviceIdRef = useRef(hasAnalyticsConsent)
  persistDeviceIdRef.current = hasAnalyticsConsent

  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(defaultFeatureFlags)
  const [flagsReady, setFlagsReady] = useState(false)

  useEffect(() => {
    if (hasAnalyticsConsent) {
      getOrCreateFeatureFlagsDeviceId(true)
    }
  }, [hasAnalyticsConsent])

  useEffect(() => {
    const client = getFeatureFlagsClient()
    if (!client) {
      setFeatureFlags(defaultFeatureFlags)
      setFlagsReady(true)
      return
    }

    let isActive = true

    const fetchFeatureFlags = async () => {
      setFeatureFlags(defaultFeatureFlags)
      setFlagsReady(false)

      try {
        const experimentClient = await client.fetch(
          createExperimentUser(auth, persistDeviceIdRef.current)
        )
        if (!isActive) return

        setFeatureFlags(resolveFeatureFlags(experimentClient.all()))
      } catch (error) {
        if (!isActive) return
        console.error('[FeatureFlags] fetch failed', error)
        setFeatureFlags(defaultFeatureFlags)
      } finally {
        if (isActive) setFlagsReady(true)
      }
    }

    void fetchFeatureFlags()

    return () => {
      isActive = false
    }
  }, [auth])

  return (
    <FeatureFlagsStatusContext.Provider value={{ flagsReady }}>
      <FeatureFlagsContext.Provider value={featureFlags}>{children}</FeatureFlagsContext.Provider>
    </FeatureFlagsStatusContext.Provider>
  )
}

export const useFeatureFlags = (): FeatureFlags => {
  const context = useContext(FeatureFlagsContext)

  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagsProvider')
  }

  return context
}

export const useFeatureFlagsStatus = (): FeatureFlagsStatus => {
  const context = useContext(FeatureFlagsStatusContext)

  if (!context) {
    throw new Error('useFeatureFlagsStatus must be used within FeatureFlagsProvider')
  }

  return context
}
